import React, { useState, useEffect } from 'react';
import ColorInit from "../helper/ColorInit";
import HeaderOne from "../components/HeaderTwo";
import BottomFooter from "../components/BottomFooter";

const FAQ = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeIndex, setActiveIndex] = useState(null);
    const [faqData, setFaqData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedSections, setExpandedSections] = useState({});

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchFAQs();
    }, []);

    const fetchFAQs = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/faq/`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch FAQs: ${response.status}`);
            }

            const data = await response.json();

            const transformedData = data.map(category => ({
                category: category.category_name,
                questions: category.faqs.map(faq => ({
                    question: faq.question,
                    answer: faq.answer
                }))
            }));

            setFaqData(transformedData);

            const initialExpandedSections = {};
            transformedData.forEach((category, catIndex) => {
                initialExpandedSections[`category_${catIndex}`] = catIndex === 0;
            });
            setExpandedSections(initialExpandedSections);

            setLoading(false);
        } catch (err) {
            setError('Failed to load FAQs');
            setLoading(false);
            console.error('Error fetching FAQs:', err);
        }
    };

    const allQuestions = faqData.flatMap((category, catIndex) =>
        category.questions.map((q, qIndex) => ({
            ...q,
            category: category.category,
            catIndex,
            qIndex,
            uniqueId: `${catIndex}_${qIndex}`
        }))
    );

    const filteredQuestions = searchTerm
        ? allQuestions.filter(
            (item) =>
                item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.category.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : [];

    const toggleQuestion = (uniqueId) => {
        setActiveIndex(activeIndex === uniqueId ? null : uniqueId);
    };

    const toggleCategory = (categoryId) => {
        setExpandedSections(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    const toggleAllCategories = (expand) => {
        const newExpandedSections = {};
        faqData.forEach((category, catIndex) => {
            newExpandedSections[`category_${catIndex}`] = expand;
        });
        setExpandedSections(newExpandedSections);
    };

    const palette = {
        lightMint: "#C3DCD8",
        softGrey: "#DBD9DA",
        mutedGold: "#C5BD96",
        mutedTeal: "#93B0AC",
        bgWhite: "#F9FAFB"
    };

    const categoryColors = [
        { border: palette.mutedTeal, bg: palette.lightMint + "20", text: palette.mutedTeal },
        { border: palette.mutedGold, bg: palette.mutedGold + "10", text: palette.mutedGold },
        { border: palette.softGrey, bg: palette.softGrey + "20", text: "#6B7280" },
    ];

    const heroStyles = {
        background: "linear-gradient(135deg, #1D2A3A 0%, #2C3E50 100%)",
        color: "white",
        padding: "80px 20px",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
        marginBottom: "40px"
    };

    const titleStyles = {
        fontSize: "clamp(2.5rem, 5vw, 3.5rem)",
        fontWeight: "800",
        marginBottom: "20px",
        color: "white",
        textShadow: "0 2px 10px rgba(0,0,0,0.1)"
    };

    if (loading) return (
        <>
            <HeaderOne />
            <div style={{ textAlign: "center", padding: "100px", color: palette.mutedTeal, backgroundColor: palette.bgWhite, minHeight: "60vh" }}>
                <p style={{ fontSize: "1.5rem" }}>Loading FAQs...</p>
            </div>
            <BottomFooter />
        </>
    );

    if (error) return (
        <>
            <HeaderOne />
            <div style={{ textAlign: "center", padding: "100px", color: palette.mutedTeal, backgroundColor: palette.bgWhite, minHeight: "60vh" }}>
                <p style={{ fontSize: "1.5rem" }}>{error}</p>
            </div>
            <BottomFooter />
        </>
    );

    return (
        <>
            <HeaderOne />
            <ColorInit color={true} />

            <div className="faq-page" style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", backgroundColor: "#FFFFFF", minHeight: "100vh" }}>
                {/* Hero Section */}
                <section className="faq-page__hero" style={heroStyles}>
                    <div className="hero-glow" style={{
                        position: "absolute",
                        top: "-50%",
                        left: "-20%",
                        width: "140%",
                        height: "200%",
                        background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
                        pointerEvents: "none"
                    }}></div>
                    <div style={{ maxWidth: "800px", margin: "0 auto", position: "relative", zIndex: 1 }}>
                        <h1 style={titleStyles}>Frequently Asked Questions</h1>
                        <p style={{ fontSize: "1.25rem", opacity: "0.95", lineHeight: "1.6", color: "white", maxWidth: "700px", margin: "0 auto" }}>
                            Find answers to common questions about our services
                        </p>
                    </div>
                </section>

                <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 20px" }}>
                    {/* Search Bar */}
                    <div style={{
                        marginBottom: "40px",
                        backgroundColor: "#FFFFFF",
                        borderRadius: "20px",
                        padding: "30px",
                        boxShadow: "0 10px 30px rgba(147, 176, 172, 0.1)",
                        border: `2px solid ${palette.lightMint}`
                    }}>
                        <div style={{ position: "relative", maxWidth: "600px", margin: "0 auto" }}>
                            <input
                                type="text"
                                placeholder="Search for answers..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "16px 50px 16px 20px",
                                    borderRadius: "12px",
                                    border: `2px solid ${palette.lightMint}`,
                                    fontSize: "16px",
                                    backgroundColor: palette.bgWhite,
                                    color: "#4B5563",
                                    outline: "none",
                                    transition: "all 0.3s ease"
                                }}
                            />
                            <span style={{ position: "absolute", right: "20px", top: "50%", transform: "translateY(-50%)", color: palette.mutedTeal, fontSize: "20px" }}>
                                <i class="fa-solid fa-magnifying-glass"></i>
                            </span>
                        </div>
                        {searchTerm && (
                            <p style={{ textAlign: "center", marginTop: "15px", color: palette.mutedTeal, fontSize: "14px", fontWeight: "600" }}>
                                Found {filteredQuestions.length} results for "{searchTerm}"
                            </p>
                        )}
                    </div>

                    {!searchTerm && (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "40px", flexWrap: "wrap", gap: "20px" }}>
                            <div style={{
                                color: palette.mutedTeal,
                                fontSize: "2.2rem",
                                fontWeight: "700",
                                paddingLeft: "10px",
                                borderLeft: `8px solid ${palette.lightMint}`
                            }}>
                                FAQ Categories
                            </div>
                            <div style={{ display: "flex", gap: "15px" }}>
                                <button
                                    onClick={() => toggleAllCategories(true)}
                                    style={{
                                        backgroundColor: palette.mutedTeal,
                                        color: "white",
                                        border: "none",
                                        padding: "10px 20px",
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                        fontWeight: "600",
                                        fontSize: "14px",
                                        transition: "all 0.3s ease",
                                        boxShadow: "0 4px 15px rgba(147, 176, 172, 0.2)"
                                    }}
                                >
                                    Expand All
                                </button>
                                <button
                                    onClick={() => toggleAllCategories(false)}
                                    style={{
                                        backgroundColor: palette.lightMint,
                                        color: palette.mutedTeal,
                                        border: "none",
                                        padding: "10px 20px",
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                        fontWeight: "600",
                                        fontSize: "14px",
                                        transition: "all 0.3s ease",
                                        boxShadow: "0 4px 15px rgba(147, 176, 172, 0.2)"
                                    }}
                                >
                                    Collapse All
                                </button>
                            </div>
                        </div>
                    )}

                    {/* FAQ Content */}
                    {!searchTerm ? (
                        <div className="faq-categories">
                            {faqData.map((category, catIndex) => {
                                const categoryId = `category_${catIndex}`;
                                const colors = categoryColors[catIndex % categoryColors.length];

                                return (
                                    <section key={catIndex} style={{ marginBottom: "50px" }}>
                                        <h2 style={{
                                            color: palette.mutedTeal,
                                            fontSize: "2.2rem",
                                            marginBottom: "30px",
                                            paddingLeft: "15px",
                                            borderLeft: `6px solid ${palette.lightMint}`,
                                            fontWeight: "700"
                                        }}>
                                            {category.category}
                                        </h2>

                                        <div style={{
                                            backgroundColor: "#FFFFFF",
                                            borderRadius: "20px",
                                            border: `2px solid ${palette.lightMint}`,
                                            boxShadow: "0 10px 30px rgba(147, 176, 172, 0.05)",
                                            borderTop: `5px solid ${colors.border}`,
                                            overflow: "hidden"
                                        }}>
                                            <div
                                                style={{
                                                    backgroundColor: colors.bg,
                                                    padding: "20px 30px",
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    cursor: "pointer",
                                                    borderBottom: expandedSections[categoryId] ? `1px solid ${palette.lightMint}` : "none"
                                                }}
                                                onClick={() => toggleCategory(categoryId)}
                                            >
                                                <h3 style={{ color: colors.text, margin: 0, fontSize: "1.3rem", fontWeight: "600" }}>
                                                    {category.category} Questions
                                                </h3>
                                                <span style={{ color: colors.text, fontWeight: "600" }}>
                                                    {expandedSections[categoryId] ? "Collapse" : "Expand"}
                                                </span>
                                            </div>

                                            <div style={{
                                                padding: expandedSections[categoryId] ? "30px" : "0",
                                                maxHeight: expandedSections[categoryId] ? "5000px" : "0",
                                                opacity: expandedSections[categoryId] ? "1" : "0",
                                                overflow: "hidden",
                                                transition: "all 0.5s ease",
                                                backgroundColor: "#FCFCFC"
                                            }}>
                                                {category.questions.map((item, qIndex) => {
                                                    const uniqueId = `${catIndex}_${qIndex}`;
                                                    const isActive = activeIndex === uniqueId;

                                                    return (
                                                        <div
                                                            key={qIndex}
                                                            style={{
                                                                marginBottom: "15px",
                                                                backgroundColor: "#FFFFFF",
                                                                borderRadius: "15px",
                                                                border: `1px solid ${isActive ? colors.border : "#E5E7EB"}`,
                                                                transition: "all 0.3s ease"
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    padding: "20px 25px",
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                    alignItems: "center",
                                                                    cursor: "pointer"
                                                                }}
                                                                onClick={() => toggleQuestion(uniqueId)}
                                                            >
                                                                <h4 style={{ color: "#374151", margin: 0, fontSize: "1.1rem", fontWeight: "600" }}>
                                                                    {item.question}
                                                                </h4>
                                                                <span style={{ color: colors.text, fontSize: "20px" }}>
                                                                    {isActive ? "−" : "+"}
                                                                </span>
                                                            </div>
                                                            {isActive && (
                                                                <div style={{ padding: "0 25px 20px 25px", color: "#6B7280", lineHeight: "1.7" }}>
                                                                    <p style={{ margin: 0 }}>{item.answer}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </section>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="faq-search-results">
                            {filteredQuestions.length > 0 ? (
                                <section style={{ marginBottom: "50px" }}>
                                    <h2 style={{ color: palette.mutedTeal, fontSize: "2rem", marginBottom: "30px", fontWeight: "700" }}>
                                        Results for "{searchTerm}"
                                    </h2>
                                    {filteredQuestions.map((item, index) => {
                                        const isActive = activeIndex === item.uniqueId;
                                        const colors = categoryColors[item.catIndex % categoryColors.length];

                                        return (
                                            <div
                                                key={index}
                                                style={{
                                                    backgroundColor: "#FFFFFF",
                                                    borderRadius: "15px",
                                                    marginBottom: "15px",
                                                    border: `1px solid ${isActive ? colors.border : "#E5E7EB"}`,
                                                    boxShadow: "0 4px 15px rgba(0,0,0,0.03)",
                                                    borderTop: `4px solid ${colors.border}`,
                                                    overflow: "hidden"
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        padding: "20px 25px",
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        cursor: "pointer"
                                                    }}
                                                    onClick={() => toggleQuestion(item.uniqueId)}
                                                >
                                                    <div>
                                                        <span style={{ color: colors.text, fontSize: "11px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "1px" }}>
                                                            {item.category}
                                                        </span>
                                                        <h3 style={{ color: "#374151", margin: "5px 0 0 0", fontSize: "1.1rem", fontWeight: "600" }}>
                                                            {item.question}
                                                        </h3>
                                                    </div>
                                                    <span style={{ color: colors.text, fontSize: "20px" }}>
                                                        {isActive ? "−" : "+"}
                                                    </span>
                                                </div>
                                                {isActive && (
                                                    <div style={{ padding: "0 25px 20px 25px", color: "#6B7280", lineHeight: "1.7" }}>
                                                        <p style={{ margin: 0 }}>{item.answer}</p>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </section>
                            ) : (
                                <div style={{ textAlign: "center", padding: "80px 20px", backgroundColor: palette.bgWhite, borderRadius: "20px" }}>
                                    <div style={{ fontSize: "50px", marginBottom: "20px" }}>🔍</div>
                                    <h3 style={{ color: palette.mutedTeal, fontSize: "1.8rem", marginBottom: "15px" }}>No results found</h3>
                                    <p style={{ color: "#8A7F8D" }}>We couldn't find any FAQs matching your search.</p>
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        style={{
                                            marginTop: "20px",
                                            backgroundColor: palette.mutedTeal,
                                            color: "white",
                                            border: "none",
                                            padding: "12px 30px",
                                            borderRadius: "8px",
                                            cursor: "pointer",
                                            fontWeight: "600"
                                        }}
                                    >
                                        Browse all FAQs
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Contact CTA */}
                    <section style={{ margin: "100px 0 80px" }}>
                        <div style={{
                            backgroundColor: palette.bgWhite,
                            borderRadius: "25px",
                            padding: "60px 40px",
                            textAlign: "center",
                            border: `2px solid ${palette.lightMint}`,
                            background: `linear-gradient(135deg, #FFFFFF 0%, ${palette.bgWhite} 100%)`
                        }}>
                            <h2 style={{ color: palette.mutedTeal, fontSize: "2.5rem", marginBottom: "20px", fontWeight: "700" }}>Still Have Questions?</h2>
                            <p style={{ color: "#6B7280", fontSize: "1.2rem", maxWidth: "600px", margin: "0 auto 40px" }}>
                                Our dedicated team is here to help! Feel free to reach out via phone or email for personalized assistance.
                            </p>
                            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "20px" }}>
                                <a href="tel:9023652806" style={{
                                    backgroundColor: palette.mutedTeal,
                                    color: "white",
                                    padding: "16px 35px",
                                    borderRadius: "12px",
                                    textDecoration: "none",
                                    fontWeight: "700",
                                    boxShadow: "0 5px 15px rgba(147, 176, 172, 0.3)",
                                    transition: "transform 0.3s ease"
                                }} onMouseEnter={e => e.currentTarget.style.transform = "translateY(-3px)"} onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                                    <i class="fa-solid fa-phone"></i>  Call Us: (902) 365-2806
                                </a>
                                <a href="mailto:info@pandaprint.ca" style={{
                                    backgroundColor: "white",
                                    color: palette.mutedTeal,
                                    border: `2px solid ${palette.mutedTeal}`,
                                    padding: "16px 35px",
                                    borderRadius: "12px",
                                    textDecoration: "none",
                                    fontWeight: "700",
                                    transition: "all 0.3s ease"
                                }} onMouseEnter={e => { e.currentTarget.style.backgroundColor = palette.mutedTeal; e.currentTarget.style.color = "white"; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = "white"; e.currentTarget.style.color = palette.mutedTeal; }}>
                                    <i class="fa-solid fa-envelope"></i>   Email Us
                                </a>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            <BottomFooter />
        </>
    );
};

export default FAQ;